import { CALL_LOGS_PER_PAGE } from 'Components/CallLogs/constants';
import { MobXProviderContext } from 'mobx-react';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useCallLogs } from 'Modules/callLogs/index.request';
import { useInfiniteContacts } from 'Modules/contacts/requests';
import React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { isPhoneNumber } from 'Utils/phoneUtil';
import { OptionType } from '~/lib/ui/autocomplete/index.types';
import { createFreeTextOption, createPhoneNumberOption } from '~/lib/ui/autocomplete/utils';
import { CallLogCategoryType, CallLogContactOption, CallLogType } from '../../types';
import { buildContactOptions } from '../utils/buildContactOption';

/**
 * react-select in multi mode accumulates all selections, so we always
 * keep only the last one to enforce single-chip behavior
 */
const getLatestSelection = (options: OptionType[]): OptionType[] =>
  options.length > 0 ? [options[options.length - 1]] : [];

// Maximum number of phone numbers the call logs search API accepts per request.
const API_PHONE_NUMBERS_LIMIT = 50;

interface UseCallLogsSearchReturn {
  allLogs: CallLogType[];
  allContactOptions: OptionType[];
  selectedOption: OptionType[];
  isLoading: boolean;
  contactsLoading: boolean;
  isFetchingNextContactsPage: boolean;
  searchQuery: string;
  hasMoreCallLogs: boolean;
  handleChange: (options: OptionType[]) => void;
  handleLoadCallLogs: () => void;
  loadMoreContacts: () => void;
  setInputValue: (value: string) => void;
}

/**
 * Hook to manage search and filtering state for CallsDirectoryList.
 *
 * Separates inputValue (drives dropdown suggestions) from searchQuery
 * (triggers call log refetches) to avoid unnecessary API calls while typing.
 *
 * State management:
 * - Resets all search state on tab change.
 * - Enforces single-chip behavior on the autocomplete.
 * - When a saved contact is selected, filters call logs by all associated phone
 *   numbers and extension numbers.
 * - For internal contacts, the person ID is also included in the search criteria.
 * - When a saved contact is selected, overrides the contact information on all returned
 *   call log entries to display the selected contact's identity consistently, even
 *   when multiple contacts share the same phone number.
 * - Tracks GA events on contact vs free-text selection.
 * - Also applies an external filter set on CallLogsStore.pendingFilter, which only happens
 *   when selecting "View call history" from the contact/conversation details panel.
 * - Defers the call-logs fetch while applying a pending filter so navigating to All Calls
 *   does not fire an unfiltered request first.
 */
export const useCallLogsSearch = (
  typeOfCallId: CallLogCategoryType
): UseCallLogsSearchReturn => {
  const {
    callLogsStore: {
      searchQuery,
      setSearchQuery,
      pendingFilter,
      clearPendingFilter,
    },
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  // Drives the contact suggestions dropdown while the user types.
  const [inputValue, setInputValue] = React.useState('');

  // Drives the call logs filter, only updated on option selection.
  const [selectedOption, setSelectedOption] = React.useState<OptionType[]>([]);

  // Drives the call logs filter by phone numbers and extensions, populated on contact selection.
  // For internal contacts, also includes sourceId to match internal call log entries.
  const [phoneNumbers, setPhoneNumbers] = React.useState<string[]>([]);

  // Stores the contact selected from the autocomplete.
  // Used to override the contact information on all returned call log entries, since a phone
  // number search may return logs belonging to different contacts who share the same number.
  const [selectedContactModel, setSelectedContactModel] = React.useState<ContactModelV2 | null>(null);

  const [personId, setPersonId] = React.useState<string | undefined>(undefined);

  // Skips the next tab-reset if a pendingFilter already existed on mount,
  // so it isn't cleared before being applied below.
  const hasPendingFilterOnMount = React.useRef(!!pendingFilter);

  // Preserve the pending filter when it arrives while not on All Calls.
  if (pendingFilter && typeOfCallId !== 'all') {
    hasPendingFilterOnMount.current = true;
  }

  // Prevents an unfiltered call-logs fetch while the pending filter is applied.
  const [isCallLogsFetchReady, setIsCallLogsFetchReady] = React.useState(
    () => !(typeOfCallId === 'all' && !!pendingFilter)
  );

  if (typeOfCallId === 'all' && pendingFilter && isCallLogsFetchReady) {
    setIsCallLogsFetchReady(false);
  }

  const {
    data: contactsData,
    isLoading: contactsLoading,
    fetchNextPage: fetchNextContactsPage,
    hasNextPage: hasNextContactsPage,
    isFetchingNextPage: isFetchingNextContactsPage,
  } = useInfiniteContacts(inputValue, CALL_LOGS_PER_PAGE);

  const {
    data: callLogs,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useCallLogs({
    typeOfCallId,
    query: searchQuery,
    phoneNumbers,
    personId,
    enabled: isCallLogsFetchReady,
  });

  // Creates a typed option for phone numbers or free text input to use as a search filter.
  const typedOption = React.useMemo((): OptionType | null => {
    if (!inputValue) {
      return null;
    }
    if (isPhoneNumber(inputValue)) {
      return createPhoneNumberOption(inputValue);
    }
    return createFreeTextOption(inputValue);
  }, [inputValue]);

  // Maps paginated contact pages into a flat list of autocomplete options.
  const contactOptions = React.useMemo(() => {
    const contacts = contactsData?.pages?.flatMap((p) => p.contacts) ?? [];
    return buildContactOptions(contacts, inputValue);
  }, [contactsData, inputValue]);

  // Prepends the typed option (free text or phone number) to the list of autocomplete
  // options, so the user can always search by exactly what they typed.
  const allContactOptions = React.useMemo(() => {
    if (!typedOption) {
      return contactOptions;
    }
    return [typedOption, ...contactOptions];
  }, [typedOption, contactOptions]);

  // Overrides the contact information on every log with the selected contact's identity.
  // Needed because selecting a saved contact searches by all its phone numbers,
  // which may return logs from other contacts sharing the same number.
  // No override for free text/phone number searches (selectedContactModel is null).
  const allLogs = React.useMemo(
    () =>
      (callLogs?.pages.flatMap((page) => page) ?? []).map((log) =>
        selectedContactModel ? { ...log, overrideContact: selectedContactModel } : log
      ),
    [callLogs, selectedContactModel]
  );

  // Requests the next page of contact suggestions when the user scrolls
  // to the bottom of the list of autocomplete options.
  const loadMoreContacts = React.useCallback(() => {
    if (hasNextContactsPage && !isFetchingNextContactsPage) {
      void fetchNextContactsPage();
    }
  }, [hasNextContactsPage, isFetchingNextContactsPage, fetchNextContactsPage]);

  // Resets all search state when the user switches between call log tabs.
  React.useEffect(() => {
    if (hasPendingFilterOnMount.current) {
      hasPendingFilterOnMount.current = false;
      return;
    }
    setSearchQuery('');
    setSelectedOption([]);
    setInputValue('');
    setPhoneNumbers([]);
    setSelectedContactModel(null);
    setPersonId(undefined);
  }, [typeOfCallId, setSearchQuery]);

  // Memoized so the pendingFilter effect below can depend on it safely.
  const handleChange = React.useCallback((options: OptionType[]) => {
    // Enforce single-chip behavior by keeping only the most recent selection.
    const latestSelection = getLatestSelection(options);
    setSelectedOption(latestSelection);

    if (latestSelection.length === 0) {
      setSearchQuery('');
      setPhoneNumbers([]);
      setSelectedContactModel(null);
      setPersonId(undefined);
      return;
    }

    const option = latestSelection[0] as CallLogContactOption;

    // Free text or typed phone number, filter by query string.
    // Strip non-numeric characters for phone number searches.
    if (option.type === 'unsaved-contact') {
      pushToGTMDataLayer('call_logs_free_search');
      const cleanedValue = option.isTypedPhoneNumber
        ? option.value.replace(/\D/g, '')
        : option.value;
      setSearchQuery(cleanedValue);
      setPhoneNumbers([]);
      setPersonId(undefined);
    } else {
      // Saved contact, filter by all associated phone numbers and extensions.
      pushToGTMDataLayer('call_logs_contact_search');
      setSearchQuery('');
      setSelectedContactModel(option.selectedContact);

      // Internal contacts require one reserved slot for the pr<sourceId> entry,
      // keeping the total within the API_PHONE_NUMBERS_LIMIT.
      const isInternalWithSourceId =
        option.type === 'internal-contact' && !!option.selectedContact.sourceId;
      const maxPhoneNumbers = isInternalWithSourceId
        ? API_PHONE_NUMBERS_LIMIT - 1
        : API_PHONE_NUMBERS_LIMIT;

      const contactPhoneNumbers = [
        ...(option.selectedContact.phoneNumbers ?? [])
          .map((p) => p.normalizedNumber)
          .filter(Boolean),
        ...(option.selectedContact.extensionNumbers ?? [])
          .map((e) => e.number),
      ].slice(0, maxPhoneNumbers);

      // Prepend pr<sourceId> for internal contacts.
      if (isInternalWithSourceId) {
        setPersonId(option.selectedContact.sourceId);
        contactPhoneNumbers.unshift(`pr${option.selectedContact.sourceId}`);
      }

      setPhoneNumbers(contactPhoneNumbers);
    }
  }, [setSearchQuery]);

  // Applies the pendingFilter set from the contact/conversation details
  // panel's "View call history" action, through the same path as a manual
  // selection. Only consumed on All Calls; if set while on another tab,
  // the filter stays pending in the store until the All Calls tab becomes
  // active, where it gets applied and cleared.
  React.useEffect(() => {
    if (!pendingFilter || typeOfCallId !== 'all') {
      return;
    }

    if (pendingFilter.type === 'contact') {
      const [contactOption] = buildContactOptions([pendingFilter.contact], '');
      if (contactOption) {
        handleChange([contactOption]);
      }
    } else {
      handleChange([createPhoneNumberOption(pendingFilter.phoneNumber)]);
    }

    setInputValue('');
    clearPendingFilter();
    setIsCallLogsFetchReady(true);
  }, [pendingFilter, handleChange, clearPendingFilter, typeOfCallId]);

  const handleLoadCallLogs = () => {
    void fetchNextPage();
  };

  return {
    allLogs,
    allContactOptions,
    selectedOption,
    isLoading: isLoading || !isCallLogsFetchReady,
    contactsLoading,
    isFetchingNextContactsPage,
    searchQuery,
    // True while more call logs can still be loaded on scroll
    hasMoreCallLogs: Boolean(hasNextPage),
    handleChange,
    handleLoadCallLogs,
    loadMoreContacts,
    setInputValue,
  };
};